import { useState } from 'react';
import {
  PauseResumeDialog,
  type LifecycleIntent,
} from '@/features/properties/lifecycle/PauseResumeDialog';
import type { ChannexUnitPublicationRow } from '@/shared/api';
import { ReconcileBindingDialog, type ReconcileTarget } from './ReconcileBindingDialog';
import type { usePublicationCentre } from './usePublicationCentre';

type Centre = ReturnType<typeof usePublicationCentre>;

/**
 * Die beiden Rückfragen der Veröffentlichung. Pausieren nimmt ein Apartment aus dem Verkauf —
 * beim Kanalmanager über den vollen Horizont und intern für neue Buchungen —, der Abgleich
 * schreibt eine Vendor-Identität fest. Beides läuft nie auf einen Klick durch.
 */
export function usePublicationDialogs(centre: Centre) {
  const [intent, setIntent] = useState<{
    propertyId: string;
    name: string;
    action: LifecycleIntent;
  } | null>(null);
  const [reconcile, setReconcile] = useState<ReconcileTarget | null>(null);

  const dialogs = (
    <>
      {reconcile && (
        <ReconcileBindingDialog
          target={reconcile}
          pending={centre.reconcile.isPending}
          onClose={() => setReconcile(null)}
          // `mutate` statt `mutateAsync`: die Ablehnung zeigt der Hook schon als Grund an; ein
          // zurückgeworfenes Versprechen bliebe im Dialog unbehandelt liegen.
          onConfirm={(action, remoteId) =>
            centre.reconcile.mutate(
              { target: reconcile, action, remoteId },
              { onSuccess: () => setReconcile(null) },
            )
          }
        />
      )}
      {intent && (
        <PauseResumeDialog
          open
          onOpenChange={(next) => !next && setIntent(null)}
          intent={intent.action}
          propertyName={intent.name}
          pending={centre.lifecycle.isPending}
          onConfirm={() =>
            centre.lifecycle.mutate(
              { propertyId: intent.propertyId, action: intent.action },
              { onSuccess: () => setIntent(null) },
            )
          }
        />
      )}
    </>
  );

  return {
    dialogs,
    askLifecycle: (unit: ChannexUnitPublicationRow, action: LifecycleIntent) =>
      setIntent({ propertyId: unit.propertyId, name: unit.name, action }),
    askReconcile: setReconcile,
  };
}
